import React, { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getActiveSubSection } from 'store/useBusinessToolStore/selectors';

import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { BorderedCard } from 'components/BorderedCard';
import {
  REVENUE_PLANS_FIELDS_NUMBER,
  SALES_PLAN_DETAIL_FIELDS_NUMBER,
} from 'components/BusinessToolForm/constants/fieldsNumber';
import { PRODUCT_NAMES } from 'components/BusinessToolForm/Sections/Global/general/products';

import { concatWithDot } from 'helpers/string';

import { MonthlySalesPlanField } from './MonthlySalesPlanField';
import { SalesVolumeInputField } from './SalesVolumeInputField';

export const SalesProductsItem: React.FC<{ name: string; disabled?: boolean }> = memo(({ name, disabled }) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const activeSubSection = useBusinessToolStore(getActiveSubSection);
  const isSalesPlan = activeSubSection === BUSINESS_TOOL_SUB_SECTIONS.MARKETING_AND_SALES.SALES_PLAN;

  return (
    <BorderedCard>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextInputControl
            control={control}
            id={concatWithDot(name, PRODUCT_NAMES.NAME)}
            name={concatWithDot(name, PRODUCT_NAMES.NAME)}
            label={t('global.products.name')}
            disabled={disabled}
            maxLength={50}
            data-mirror-field={concatWithDot(
              name,
              `${PRODUCT_NAMES.NAME}.${isSalesPlan ? SALES_PLAN_DETAIL_FIELDS_NUMBER : REVENUE_PLANS_FIELDS_NUMBER}`,
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <NumberInputControlled
            control={control}
            id={concatWithDot(name, PRODUCT_NAMES.PRICE)}
            name={concatWithDot(name, PRODUCT_NAMES.PRICE)}
            label={t('global.products.price')}
            adornmentType="currency"
            disabled={disabled}
            data-mirror-field={concatWithDot(
              name,
              `${PRODUCT_NAMES.PRICE}.${isSalesPlan ? SALES_PLAN_DETAIL_FIELDS_NUMBER : REVENUE_PLANS_FIELDS_NUMBER}`,
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <SalesVolumeInputField name={name} disabled={disabled ?? false} />
        </Grid>
        <Grid item xs={6}>
          <MonthlySalesPlanField name={name} />
        </Grid>
        <Grid item xs={6}>
          <NumberInputControlled
            control={control}
            id={concatWithDot(name, PRODUCT_NAMES.ANNUAL_PRICE_GROWTH_RATE)}
            name={concatWithDot(name, PRODUCT_NAMES.ANNUAL_PRICE_GROWTH_RATE)}
            label={t('global.products.annualPriceGrowth')}
            adornmentType="percentage"
            disabled={disabled}
            data-mirror-field={
              isSalesPlan
                ? ''
                : concatWithDot(name, `${PRODUCT_NAMES.ANNUAL_PRICE_GROWTH_RATE}.${REVENUE_PLANS_FIELDS_NUMBER}`)
            }
          />
        </Grid>
        <Grid item xs={6}>
          <NumberInputControlled
            control={control}
            id={concatWithDot(name, PRODUCT_NAMES.ANNUAL_SALES_GROWTH_RATE)}
            name={concatWithDot(name, PRODUCT_NAMES.ANNUAL_SALES_GROWTH_RATE)}
            label={t('global.products.annualSalesGrowth')}
            adornmentType="percentage"
            disabled={disabled}
            data-mirror-field={concatWithDot(
              name,
              `${PRODUCT_NAMES.ANNUAL_SALES_GROWTH_RATE}.${isSalesPlan ? SALES_PLAN_DETAIL_FIELDS_NUMBER : REVENUE_PLANS_FIELDS_NUMBER}`,
            )}
          />
        </Grid>
      </Grid>
    </BorderedCard>
  );
});
